import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';

// Relativo por padrao: em producao a API e servida no mesmo host do cliente
// (https://cliente.membros.x-treed.com/api/v1), o que elimina CORS e faz o
// Host carregar o tenant. Em dev o proxy do Vite encaminha para :3333.
const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track refresh state to avoid multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Helper: extract tokens from response (handles TransformInterceptor wrapper)
const extractTokens = (data: Record<string, unknown>) => {
  const inner = (data?.data as Record<string, unknown>) || data;
  return (
    (inner?.tokens as {
      accessToken: string;
      refreshToken: string;
      expiresIn?: number;
    }) || null
  );
};

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Em producao o tenant vem do Host (subdominio ou dominio proprio do
    // cliente). Em dev tudo roda em localhost, entao o Host nao distingue
    // ninguem: sem este header o backend fica sem contexto e ate o login falha.
    if (import.meta.env.DEV) {
      // Espelha bootstrap-tenant: sem cliente escolhido, nenhuma marca —
      // as rotas de plataforma nao querem contexto de tenant mesmo.
      const slug = localStorage.getItem('xt:dev-tenant');
      if (slug) {
        config.headers['x-tenant-slug'] = slug;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - handle token refresh + deploy resilience
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    // Retry on network errors or 502/503 (deploy in progress)
    const status = error.response?.status;
    if (
      (!status || status === 502 || status === 503) &&
      !originalRequest._retry
    ) {
      const retryCount = originalRequest._retryCount || 0;
      if (retryCount < 3) {
        originalRequest._retryCount = retryCount + 1;
        // Wait 2s, 4s, 6s between retries
        await new Promise((r) => setTimeout(r, (retryCount + 1) * 2000));
        return api(originalRequest);
      }
    }

    // Handle 401 - try refresh token (only for authenticated requests)
    const isAuthRequest = originalRequest.url?.includes('/auth/');
    if (status === 401 && !originalRequest._retry && !isAuthRequest) {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) {
        // Only logout if user was previously authenticated
        if (useAuthStore.getState().isAuthenticated) {
          useAuthStore.getState().logout();
        }
        return Promise.reject(error);
      }

      // If already refreshing, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const tokens = extractTokens(data);
        if (!tokens) {
          throw new Error('No tokens in refresh response');
        }

        useAuthStore
          .getState()
          .setTokens({ ...tokens, expiresIn: tokens.expiresIn || 432000 });
        originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;

        processQueue(null, tokens.accessToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {
      refreshToken,
    });
    const tokens = extractTokens(data);
    if (!tokens) return null;
    useAuthStore
      .getState()
      .setTokens({ ...tokens, expiresIn: tokens.expiresIn || 432000 });
    return tokens.accessToken;
  } catch {
    return null;
  }
}

export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = useAuthStore.getState().accessToken;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (import.meta.env.DEV) {
    const slug = localStorage.getItem('xt:dev-tenant');
    if (slug) headers['x-tenant-slug'] = slug;
  }
  return headers;
}

export const API_BASE_URL = BASE_URL;

// Proactive token refresh - refresh before expiry
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

export const scheduleTokenRefresh = (expiresIn: number) => {
  if (refreshTimer) clearTimeout(refreshTimer);

  // Refresh at 80% of token lifetime (e.g., 5d token → refresh after 4d)
  const refreshAt = Math.max(expiresIn * 0.8 * 1000, 60000); // min 1 minute

  refreshTimer = setTimeout(async () => {
    const { refreshToken, isAuthenticated } = useAuthStore.getState();
    if (!refreshToken || !isAuthenticated) return;

    try {
      const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {
        refreshToken,
      });
      const tokens = extractTokens(data);
      if (tokens) {
        useAuthStore
          .getState()
          .setTokens({ ...tokens, expiresIn: tokens.expiresIn || 432000 });
        // Schedule next refresh
        scheduleTokenRefresh(expiresIn);
      }
    } catch {
      // Silent fail - the 401 interceptor will handle it on next request
    }
  }, refreshAt);
};

export const clearTokenRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

export { api };
